import * as React from "react";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import InboxIcon from "@mui/icons-material/MoveToInbox";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import AddIcon from "@mui/icons-material/Add";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import TableViewIcon from "@mui/icons-material/TableView";
import FactoryIcon from "@mui/icons-material/Factory";
import SummarizeIcon from "@mui/icons-material/Summarize";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Avatar, Menu, MenuItem, Stack, Badge } from "@mui/material";
import { NavLink } from "react-router-dom";
import AdminBanner from "../admin/admin-banner";
import Logo from "../../../assests/logos/primary-logo-white.svg";
import { ReactComponent as HomeLogo } from "../../../assests/img/Nav-Home.svg";
import { ReactComponent as TcLogo } from "../../../assests/img/Nav-TC.svg";
import { ReactComponent as ScLogo } from "../../../assests/img/Nav-SC.svg";
import { ReactComponent as ML } from "../../../assests/img/Material-Ledger.svg";
import { ReactComponent as IU } from "../../../assests/img/Inventory-Update.svg";
import { ReactComponent as MSFRMDF } from "../../../assests/img/MDF-RMDF.svg";
import { ReactComponent as RMS } from "../../../assests/img/Raw-Material-Summary.svg";
import { ReactComponent as ATC } from "../../../assests/img/ATC-Logo.svg";
import MarkAsUnreadOutlinedIcon from '@mui/icons-material/MarkAsUnreadOutlined';
import GridViewIcon from "@mui/icons-material/GridView";
import DashboardIcon from "@mui/icons-material/Dashboard";
import { useTransactionCertificateStore } from "../../../store/transaction-certificates.store";
import { useMsal } from "@azure/msal-react";
import AddHomeWorkIcon from "@mui/icons-material/AddHomeWork";
import Tooltip from "@mui/material/Tooltip";
import { LiveHelpOutlined, Notifications } from "@mui/icons-material";
import VersionSelectorPopup from "../../../screens/home/versionSelector";
import { extractTEIdFromEmail } from "../../utils/user-utils";
import { useReleaseNotes } from "../../hooks/useReleaseNotes";
import ReleaseNotesModal from "../modals/release-notes-modal";
import { AppConfig } from "../../app-configs/app-config";
import { isAdmin } from "../../utils/admin-utils";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";

const drawerWidth = 240;

export const LogoContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
});

// Mixin for opened drawer styles
const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  backgroundColor: "#FFFFFF",
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const appVersion = process.env.REACT_APP_VERSION;
const appEnv = process.env.REACT_APP_ENV;

// Mixin for closed drawer styles
const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  backgroundColor: "#FFFFFF",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

// Styled component for DrawerHeader
export const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

// Extend AppBarProps to include open and bgColor
interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
  bgColor?: string;
}

// Styled component for AppBar with dynamic background color
const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "bgColor",
})<AppBarProps>(({ theme, bgColor }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: bgColor || theme.palette.primary.main,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
}));

// Styled component for Drawer
const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

// Styled component for ListItemText with dynamic color
const CustomListItemText = styled(ListItemText)<{ textColor?: string; allowWrap?: boolean }>(
  ({ textColor, allowWrap }) => ({
    color: textColor || "inherit",
    "& .MuiListItemText-primary": {
      whiteSpace: allowWrap ? "normal" : "nowrap",
      wordWrap: allowWrap ? "break-word" : "normal",
      lineHeight: allowWrap ? 1.2 : "normal",
      overflow: allowWrap ? "visible" : "hidden",
      textOverflow: allowWrap ? "visible" : "ellipsis",
    },
  })
);

// Styled IconButton with dynamic color
const CustomIconButton = styled(IconButton)<{ iconColor?: string }>(
  ({ iconColor }) => ({
    color: iconColor || "inherit",
  })
);

export default function MiniDrawer() {
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const [showVersionModal, setShowVersionModal] = React.useState(false);
  const [selectedSource, setSelectedSource] = React.useState("");
  const [expandedItems, setExpandedItems] = React.useState<Record<string, boolean>>({});
  const [showReleaseNotes, setShowReleaseNotes] = React.useState(false);
  const { getUserInfo, setUserInfo, setAuthToken, cbLicensingCode, originalAdminCBID } =
    useTransactionCertificateStore();
  const { instance } = useMsal();
  const location = useLocation();
  const { hasNewReleases, markLatestAsViewed } = useReleaseNotes();

  // Check if current route is v4
  const isV4 = location.pathname.includes('/v4/');
  
  // State to track if user is a CO (Certified Organization)
  const [isCOUser, setIsCOUser] = React.useState(false);
  const [extractedTEId, setExtractedTEId] = React.useState<string | null>(null);
  
  // Track admin status reactively - recompute when originalAdminCBID changes
  const userIsAdmin = React.useMemo(() => {
    const adminStatus = isAdmin();
    return adminStatus;
  }, [originalAdminCBID]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };
  
  const handleVersionSelectorOpen = (source: string) => {
    setSelectedSource(source);
    const isScopeCertificate = source === "Scope Certificate";
    const shouldShowSelector = isScopeCertificate ? AppConfig.SHOW_VERSION_SELECTOR_SC : AppConfig.SHOW_VERSION_SELECTOR_TC;
    
    if (shouldShowSelector) {
      setShowVersionModal(true);
    } else {
      // Navigate directly to v3 dashboard when modal is disabled
      const defaultPath = isScopeCertificate ? '/sc-dashboard' : '/tc-dashboard';
      
      // Try React Router navigation first
      navigate(defaultPath, { replace: true });
      
      // Fallback: Force navigation using window.location
      setTimeout(() => {
        window.location.href = defaultPath;
      }, 100);
    }
  };

  const handleParentClick = (itemText: string) => {
    if (!open) {
      setOpen(true);
    }
    setExpandedItems(prev => ({
      ...prev,
      [itemText]: !prev[itemText]
    }));
  };

  const handleReleaseNotesOpen = () => {
    setShowReleaseNotes(true);
    // Hide badge when modal opens
    markLatestAsViewed();
  };

  const handleReleaseNotesClose = () => {
    setShowReleaseNotes(false);
  };


  
  const navigate = useNavigate();

  React.useEffect(() => {
  }, [getUserInfo]);
  // CO User Detection
  React.useEffect(() => {
    const userEmail = cbLicensingCode;
    if (userEmail) {
      const extractedTEId = extractTEIdFromEmail(userEmail);
      setExtractedTEId(extractedTEId);
      setIsCOUser(!!extractedTEId);
    }
  }, [cbLicensingCode]);

  // Route Protection for CO Users
  // IMPORTANT: Don't apply restrictions if user is admin viewing a CO's data
  React.useEffect(() => {
    if (isCOUser && !userIsAdmin) {
      const currentPath = window.location.pathname;
      const allowedPaths = [
        '/home',
        '/ml/material-ledger',
        '/ml-form',
        // Allow dynamic routes for material ledger
        '/ml/material-ledger/',
        '/ml-form/'
      ];
      
      const isAllowed = allowedPaths.some(path => 
        currentPath === path || currentPath.startsWith(path)
      );
      
      if (!isAllowed) {
        navigate('/home');
      }
    }
  }, [isCOUser, userIsAdmin, navigate]);

  const navItems = [
    { text: "Home", url: "/home", icon: <HomeLogo /> },
    ...(userIsAdmin ? [{
      text: "Admin Dashboard",
      url: "/admin", icon: <AdminPanelSettingsIcon />,
      adminOnly: true,
      tooltip: "Manage CB-IDs and view data for all organizations"
    }] : []),
    // { 
    //   text: "Transaction Certificate", 
    //   url: isV4 ? "/v4/tc-dashboard" : "/tc-dashboard", 
    //   icon: <TcLogo />,
    //   showVersionSelector: true
    // },
    // { 
    //   text: "Scope Certificate", 
    //   url: isV4 ? "/v4/sc-dashboard" : "/sc-dashboard", 
    //   icon: <ScLogo />, 
    //   disabled: false,
    //   showVersionSelector: true
    // },
    // { text: "Transaction Certificate", url: "/tc-dashboard", icon: <TcLogo /> },
    // { text: "Scope Certificate", url: "/sc-dashboard", icon: <ScLogo />, disabled: false },
    { 
      text: "Transaction Certificate", 
      url: isV4 ? "/v4/tc-dashboard" : "/tc-dashboard", 
      icon: <TcLogo />,
      showVersionSelector: AppConfig.SHOW_VERSION_SELECTOR_TC
    },
    { 
      text: "Scope Certificate", 
      url: isV4 ? "/v4/sc-dashboard" : "/sc-dashboard", 
      icon: <ScLogo />, 
      disabled: false,
      showVersionSelector: AppConfig.SHOW_VERSION_SELECTOR_SC
    },
    // { text: "Transaction Certificate", url: "/tc-dashboard", icon: <TcLogo /> },
    // { text: "Scope Certificate", url: "/sc-dashboard", icon: <ScLogo />, disabled: false },
    // { text: "Suppliers", url: "/suppliers", icon: <GridViewIcon />, disabled: false },
    { 
      text: "Material Ledger", 
      icon: <ML />, 
      disabled: false,
      hasChildren: true,
      children: [
        { text: "Material Ledger Summary", url: "ml/material-ledger", icon: <ML />, disabled: false },
        { text: "Inventory Updates", url: "/ml/inventory-updates", icon: <IU />, disabled: false },
        { text: "Raw Material Summary", url: "/ml-form", icon: <RMS />, disabled: false },
        { text: "Alternative Transaction Upload", url: "/atc", icon: <ATC />, disabled: false },
        { text: "Material Ledger Report", url: "/ml-report", icon: <RMS />, disabled: false }
      ]
    },
    // { text: "RMDF Submission", url: "/ml-form/rmdf/mdf", icon: <MSFRMDF />, disabled: false },
    {
      text: "Questions / Support",
      url: "https://textileexchange.my.site.com/s/sctcsupport",
      icon: <LiveHelpOutlined />,
      external: true,
    },

        {
      text: "Release Notes",
      icon: (
        <Badge
          variant="dot"
          color="error"
          invisible={!hasNewReleases}
          sx={{
            '& .MuiBadge-badge': {
              right: -2,
              top: 2,
              width: 8,
              height: 8,
            },
          }}
        >
          <Notifications />
        </Badge>
      ),
      onClick: handleReleaseNotesOpen,
      isAction: true,
    },
    // { text: 'MDF Submission', url: '/ml-form/rmdf/mdf', icon: <SummarizeIcon /> }
  ];

  // Filter navigation items for CO users
  // IMPORTANT: If user is admin, don't apply CO filtering even if viewing a CO's TE-ID
  const allowedCOItems = [
    "Home",
    "Material Ledger", 
    "Raw Material Summary",
    "Material Ledger Summary",
    "Questions / Support",
    "Release Notes"
  ];

  const filteredNavItems = (isCOUser && !userIsAdmin)
    ? navItems.map(item => {
        if (item.text === "Material Ledger" && item.hasChildren) {
          return {
            ...item,
            children: item.children?.filter(child => allowedCOItems.includes(child.text))
          };
        }
        return allowedCOItems.includes(item.text) ? item : null;
      }).filter((item): item is NonNullable<typeof item> => item !== null)
    : navItems;

  const [anchorEl, setAnchorEl] = React.useState(null);

  // Opens the menu on click
  const handleMenuOpen = (event: any) => {
    setAnchorEl(event.currentTarget);
  };
  // Handle logout action
  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    instance.logoutRedirect();
  };
  // Closes the menu
  const handleMenuClose = (options?: any) => {
    if (options == "Logout") {
      handleLogout();
    }
    setAnchorEl(null);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <CssBaseline />
      <AppBar position="fixed" bgColor="#353C52">
        <Toolbar>
          <CustomIconButton
            iconColor="white"
            aria-label="open drawer"
            onClick={open ? handleDrawerClose : handleDrawerOpen}
            edge="start"
            sx={{
              marginX: 2,
              mb: "6px",
            }}
          >
            {open ? <ChevronLeftIcon /> : <MenuIcon />}
          </CustomIconButton>

          <LogoContainer sx={{ marginRight: "auto" }}>
            <NavLink to="/home">
              <Box
                component="img"
                src={Logo}
                alt="Logo"
                sx={{
                  width: "auto",
                  height: "auto",
                }}
              />
            </NavLink>
          </LogoContainer>

          <Box sx={{ flexGrow: 1 }} />

          {/* Release Notes Bell Icon */}
          <Box sx={{ position: 'relative', display: 'inline-block' }}>
            <IconButton
              onClick={handleReleaseNotesOpen}
              sx={{ 
                color: 'white',
                mr: 1,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
            >
              <Badge
                variant="dot"
                color="error"
                invisible={!hasNewReleases}
                sx={{
                  '& .MuiBadge-badge': {
                    right: 3,
                    top: 3,
                    width: 8,
                    height: 8,
                  },
                }}
              >
                <Notifications />
              </Badge>
            </IconButton>
            
            {/* Always visible tooltip when badge is present */}
            {hasNewReleases && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  mt: 1,
                  px: 1.5,
                  py: 0.5,
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  color: 'white',
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  zIndex: 1300,
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -4,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 0,
                    height: 0,
                    borderLeft: '4px solid transparent',
                    borderRight: '4px solid transparent',
                    borderBottom: '4px solid rgba(0, 0, 0, 0.8)',
                  },
                }}
              >
                Click to see new version features.
              </Box>
            )}
          </Box>

          {/* Avatar and Email */}
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton onClick={handleMenuOpen} sx={{ p: 0 }}>
              <Avatar
                alt={getUserInfo.userEmail}
                src="/path-to-avatar-image.jpg"
              />
            </IconButton>
            <Typography
              variant="body1"
              color="white"
              sx={{ cursor: "pointer" }}
              onClick={handleMenuOpen}
            >
              {getUserInfo.userEmail}
            </Typography>
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
            PaperProps={{
              sx: {
                borderRadius: 2,
                minWidth: 150,
                backgroundColor: "#fff",
                color: "#1c1f2a",
              },
            }}
          >
            {["Logout"].map((label, index) => (
              <MenuItem
                key={index}
                onClick={() => {
                  handleMenuClose(label);
                }}
                sx={{
                  fontSize: "0.9rem",
                  fontWeight: "500",
                  "&:hover": {
                    backgroundColor: "#33364a",
                    color: "#fff",
                  },
                }}
              >
                {label}
              </MenuItem>
            ))}
            <Divider />
            <Typography
              variant="body2"
              sx={{
                borderRadius: 2,
                minWidth: 150,
                backgroundColor: "#fff",
                color: "#1c1f2a",
                paddingLeft: 3,
              }}
              alignItems="center"
            >
              Verison: {appEnv}-{appVersion}
            </Typography>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: "flex", flexGrow: 1 }}>
        <Drawer variant="permanent" open={open}>
          <Stack flexDirection="column" justifyContent="space-between">
            <Divider />
            <List>
              <DrawerHeader />
              {filteredNavItems.map((item, index) => (
                <React.Fragment key={item.text}>
                  <ListItem
                    disablePadding
                    sx={{ display: "block" }}
                  >
                    {item.disabled ? (
                      // Disabled item - no navigation
                      !open ? (
                        <Tooltip title={item.text} placement="right">
                          <ListItemButton
                            sx={{
                              minHeight: 48,
                              justifyContent: "center",
                              px: 2.5,
                              opacity: 0.3,
                              cursor: "not-allowed",
                            }}
                            disabled
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 24,
                                mr: "auto",
                                justifyContent: "center",
                                opacity: 1,
                              }}
                            >
                              {item.icon}
                            </ListItemIcon>
                            <CustomListItemText
                              primary={item.text}
                              sx={{ opacity: 0 }}
                              textColor={"#888"}
                              allowWrap={false}
                            />
                          </ListItemButton>
                        </Tooltip>
                      ) : (
                        <ListItemButton
                          sx={{
                            minHeight: 48,
                            justifyContent: open ? "initial" : "center",
                            px: 2.5,
                            opacity: 1,
                            cursor: "not-allowed",
                          }}
                          disabled
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 24,
                              mr: open ? 3 : "auto",
                              justifyContent: "center",
                              opacity: 1,
                            }}
                          >
                            {item.icon}
                          </ListItemIcon>
                          <CustomListItemText
                            primary={item.text}
                            sx={{ opacity: 1 }}
                            textColor={"#888"}
                            allowWrap={true}
                          />
                        </ListItemButton>
                      )
                    ) : item.hasChildren ? (
                      // Parent item with children
                      !open ? (
                        <Tooltip title={item.text} placement="right">
                          <ListItemButton
                            onClick={() => handleParentClick(item.text)}
                            sx={{
                              minHeight: 48,
                              justifyContent: "center",
                              px: 2.5,
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 24,
                                mr: "auto",
                                justifyContent: "center",
                              }}
                            >
                              {item.icon}
                            </ListItemIcon>
                            <CustomListItemText
                              primary={item.text}
                              sx={{ opacity: 0 }}
                              textColor={"#1C1C1C"}
                              allowWrap={false}
                            />
                          </ListItemButton>
                        </Tooltip>
                      ) : (
                        <ListItemButton
                          onClick={() => handleParentClick(item.text)}
                          sx={{
                            minHeight: 48,
                            justifyContent: open ? "initial" : "center",
                            px: 2.5,
                            display: "flex",
                            alignItems: "flex-start",
                            py: 1,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 24,
                              mr: open ? 3 : "auto",
                              justifyContent: "center",
                              alignItems: "flex-start",
                              pt: 0.5,
                            }}
                          >
                            {item.icon}
                          </ListItemIcon>
                          <CustomListItemText
                            primary={item.text}
                            sx={{ 
                              opacity: 1,
                              flex: 1,
                              minWidth: 0, // Allow text to shrink
                            }}
                            textColor={"#1C1C1C"}
                            allowWrap={true}
                          />
                          {open && (
                            <Box sx={{ 
                              ml: 1,
                              flexShrink: 0,
                              display: "flex",
                              alignItems: "flex-start",
                              pt: 0.5,
                            }}>
                              {expandedItems[item.text] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </Box>
                          )}
                        </ListItemButton>
                      )
                    ) : item.showVersionSelector ? (
                      // Version selector item
                      <ListItemButton
                        onClick={() => handleVersionSelectorOpen(item.text)}
                        sx={{
                          minHeight: 48,
                          justifyContent: open ? "initial" : "center",
                          px: 2.5,
                          alignItems: "flex-start",
                          py: 1,
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 24,
                            mr: open ? 3 : "auto",
                            justifyContent: "center",
                            alignItems: "flex-start",
                            pt: 0.5,
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <CustomListItemText
                          primary={item.text}
                          sx={{ opacity: open ? 1 : 0 }}
                          textColor={"#1C1C1C"}
                          allowWrap={open}
                        />
                      </ListItemButton>
                    ) : item.isAction ? (
                      // Action item (like Release Notes)
                      !open ? (
                        <Tooltip title={item.text} placement="right">
                          <ListItemButton
                            onClick={item.onClick}
                            sx={{
                              minHeight: 48,
                              justifyContent: "center",
                              px: 2.5,
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 24,
                                mr: "auto",
                                justifyContent: "center",
                              }}
                            >
                              {item.icon}
                            </ListItemIcon>
                            <CustomListItemText
                              primary={item.text}
                              sx={{ opacity: 0 }}
                              textColor={"#1C1C1C"}
                              allowWrap={false}
                            />
                          </ListItemButton>
                        </Tooltip>
                      ) : (
                        <ListItemButton
                          onClick={item.onClick}
                          sx={{
                            minHeight: 48,
                            justifyContent: open ? "initial" : "center",
                            px: 2.5,
                            alignItems: "flex-start",
                            py: 1,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 24,
                              mr: open ? 3 : "auto",
                              justifyContent: "center",
                              alignItems: "flex-start",
                              pt: 0.5,
                            }}
                          >
                            {item.icon}
                          </ListItemIcon>
                          <CustomListItemText
                            primary={item.text}
                            sx={{ opacity: 1 }}
                            textColor={"#1C1C1C"}
                            allowWrap={true}
                          />
                        </ListItemButton>
                      )
                    ) : (
                      // Regular navigation item
                      <NavLink
                        to={item?.external ? "/" : (item.url || "/")}
                        onClick={() => {
                          if (item?.external) {
                            window.open(item?.url, "_blank", "noopener,noreferrer");
                          }
                        }}
                        style={{ textDecoration: "none" }}
                      >
                        {!open ? (
                          <Tooltip title={item.text} placement="right">
                            <ListItemButton
                              sx={{
                                minHeight: 48,
                                justifyContent: "center",
                                px: 2.5,
                              }}
                            >
                              <ListItemIcon
                                sx={{
                                  minWidth: 24,
                                  mr: "auto",
                                  justifyContent: "center",
                                }}
                              >
                                {item.icon}
                              </ListItemIcon>
                              <CustomListItemText
                                primary={item.text}
                                sx={{ opacity: 0 }}
                                textColor={"#1C1C1C"}
                                allowWrap={false}
                              />
                            </ListItemButton>
                          </Tooltip>
                        ) : (
                          <ListItemButton
                            sx={{
                              minHeight: 48,
                              justifyContent: open ? "initial" : "center",
                              px: 2.5,
                              alignItems: "flex-start",
                              py: 1,
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 24,
                                mr: open ? 3 : "auto",
                                justifyContent: "center",
                                alignItems: "flex-start",
                                pt: 0.5,
                              }}
                            >
                              {item.icon}
                            </ListItemIcon>
                            <CustomListItemText
                              primary={item.text}
                              sx={{ opacity: 1 }}
                              textColor={"#1C1C1C"}
                              allowWrap={true}
                            />
                          </ListItemButton>
                        )}
                      </NavLink>
                    )}
                  </ListItem>
                  
                  {/* Render children if parent is expanded */}
                  {item.hasChildren && expandedItems[item.text] && open && item.children && (
                    <List sx={{ 
                      pl: 2,
                      py: 0,
                      borderLeft: "2px solid rgba(0, 0, 0, 0.1)",
                      ml: 1,
                      "& .MuiListItem-root": {
                        py: 0.5,
                      }
                    }}>
                      {item.children.map((child) => (
                        <ListItem
                          key={child.text}
                          disablePadding
                          sx={{ display: "block" }}
                        >
                          {child.disabled ? (
                            <ListItemButton
                              sx={{
                                minHeight: 36,
                                justifyContent: "initial",
                                px: 2.5,
                                py: 0.5,
                                opacity: 0.3,
                                cursor: "not-allowed",
                                borderRadius: 1,
                                alignItems: "flex-start",
                              }}
                              disabled
                            >
                              <ListItemIcon
                                sx={{
                                  minWidth: 24,
                                  mr: 3,
                                  justifyContent: "center",
                                  opacity: 1,
                                  alignItems: "flex-start",
                                  pt: 0.5,
                                }}
                              >
                                {child.icon}
                              </ListItemIcon>
                              <CustomListItemText
                                primary={child.text}
                                textColor={"#888"}
                                allowWrap={true}
                              />
                            </ListItemButton>
                          ) : (
                            <NavLink
                              to={child.url}
                              style={{ textDecoration: "none" }}
                            >
                              <ListItemButton
                                sx={{
                                  minHeight: 36,
                                  justifyContent: "initial",
                                  px: 2.5,
                                  py: 0.5,
                                  borderRadius: 1,
                                  alignItems: "flex-start",
                                  "&:hover": {
                                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                                  },
                                }}
                              >
                                <ListItemIcon
                                  sx={{
                                    minWidth: 24,
                                    mr: 3,
                                    justifyContent: "center",
                                    alignItems: "flex-start",
                                    pt: 0.5,
                                  }}
                                >
                                  {child.icon}
                                </ListItemIcon>
                                <CustomListItemText
                                  primary={child.text}
                                  textColor={"#1C1C1C"}
                                  allowWrap={true}
                                />
                              </ListItemButton>
                            </NavLink>
                          )}
                        </ListItem>
                      ))}
                    </List>
                  )}
                </React.Fragment>
              ))}
            </List>
          </Stack>
        </Drawer>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            m: 1,
            bgcolor: "#FFFFFF",
            border: "2px solid rgba(128, 128, 128, .2)",
          }}
        >
          <DrawerHeader />
          <Box
          sx={{
            p: 2,
            mb: 2,
            bgcolor: "#FFF3E0",
            borderRadius: 1,
            border: "1px solid #FFB74D",
          }}
        >
          <Typography color="warning.dark">
            Important: This interface is for practice and demonstration purposes only. All data is sample data and real certificates cannot be submitted here.
          </Typography>
        </Box>
          <AdminBanner />
          <Box sx={{ flexGrow: 1, p: 1, m: 1 }}>
            {/* border: '2px solid grey', m: 1, bgcolor: "white" }}> */}
            <Outlet />
          </Box>
        </Box>
      </Box>
      
      {/* Footer with Copyright */}
      <Box
        component="footer"
        sx={{
          py: 2,
          px: 3,
          mt: "auto",
          backgroundColor: "#353c52",
          borderTop: "1px solid rgba(128, 128, 128, 0.2)",
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            py: 2,
            px: 3,
            backgroundColor: "#353C52",
            borderTop: "1px solid rgba(128, 128, 128, 0.2)",
            ml: { xs: open ? `${drawerWidth}px` : "64px" }, // Adjust margin-left based on drawer state
            width: {
              xs: open ? `calc(100% - ${drawerWidth}px)` : "calc(100% - 64px)",
            }, // Adjust width
          }}
        >
          <Typography
            variant="body2"
            color="white"
            sx={{ textAlign: "left", marginRight: "auto" }}
          >
            {isCOUser ? (
              `CO TE-ID: ${extractedTEId || 'TE-00123006'}`
            ) : (
              cbLicensingCode ? `CB-Licensing Code: ${cbLicensingCode}` : ''
            )}
          </Typography>
          <Typography
            variant="body2"
            color="white"
            sx={{ flex: 1, textAlign: "center" }}
          >
            © {new Date().getFullYear()} Textile Exchange. All rights reserved.
          </Typography>
          <Typography
            variant="body2"
            color="white"
            sx={{ textAlign: "right", marginLeft: "auto" }}
          >
            For questions or support, please go  <NavLink
                    to={"/"}
                    onClick={() => {
                        window.open("https://textileexchange.my.site.com/s/sctcsupport", "_blank", "noopener,noreferrer");
                    }}
                    style={{ textDecoration: "underline", color:"#FFFFFF" }}
                  >
                    here</NavLink>
          </Typography>
        </Box>
      </Box>
      
      {/* Version Selector Modal */}
      {(AppConfig.SHOW_VERSION_SELECTOR_TC || AppConfig.SHOW_VERSION_SELECTOR_SC) && (
        <VersionSelectorPopup
          open={showVersionModal}
          onClose={() => setShowVersionModal(false)}
          source={selectedSource}
          versionLinks={{
            v3: selectedSource === "Scope Certificate" ? '/sc-dashboard' : "/tc-dashboard",
            v4: selectedSource === "Scope Certificate" ? '/v4/sc-dashboard' : "/v4/tc-dashboard",
          }}
        />
      )}

      {/* Release Notes Modal */}
      <ReleaseNotesModal
        open={showReleaseNotes}
        onClose={handleReleaseNotesClose}
      />
    </Box>
  );
}